import { ENDPOINTS, WorkerType } from './types';
import { environmentService } from './environmentService';
import { getIdToken } from './authToken';
import type {
  CustomerSalesSection,
  QuoteForecast,
  QuoteToCashView,
  YtdSection,
} from './quoteToCashTypes';
import type { PartDemand } from './partDemandTypes';
import type {
  SalesAgentApprovalRequest,
  SalesAgentCategory,
  SalesAgentEmails,
} from './salesAgentTypes';
import type {
  ListParserRequest,
  PrinterJobRequest,
  JobRequest,
  JobSubmitResponse,
  JobStatusResponse,
  JobResultResponse,
  GetPartsResponse,
  GetPartDetailsResponse,
  GetPartBinsResponse,
  GetPartTimePhasedResponse,
  GetPartPurchaseHistResponse,
  GetPartApprovedSuppliersResponse,
  GetPartTransHistResponse,
  GetPartQuoteHistResponse,
  GetPartCustomerRefsResponse,
  GetPartOrderHistResponse,
  GetPartProdHistResponse,
  GetCustomerPriceResponse,
  Part,
  PartDetails,
  PartBin,
  PartTimePhased,
  PurchaseHist,
  ApprovedSupplier,
  QuoteHistory,
  CustomerRef,
  OrderHistory,
  ProdHistory,
  CustomerPriceList,
  PaginationMeta,
  GetFulfillmentItemsResponse,
  FulfillmentItem,
  FulfillmentPaginationMeta,
  GetMethodResponse,
  MethodNode,
  PrinterPreviewRequest,
  LookupUMPartResponse,
  GetWhereUsedResponse,
  WhereUsed,
  GetSalesOrderResponse,
  SalesOrder,
  GetSalesOrderShipmentsResponse,
  SalesOrderShipment,
  GetPurchaseOrderResponse,
  PurchaseOrder,
  GetPurchaseOrderReceiptsResponse,
  PurchaseOrderReceipt,
  GetProductionScheduleResponse,
  SchedulingBoardEntry,
  PriceList,
  GetPriceListsResponse,
  ListParserDataItem,
  ListParserDataQuery,
  GetListParserDataResponse,
  GetJobDataResponse,
  GetJobPORelsResponse,
  PORelInfo,
  JobDataset,
  Bin,
  GetBinsResponse,
  ProductionCalendar,
  GetProductionCalendarResponse,
  SubmitPictureResponse,
  PictureItem,
  GetRecentPicturesResponse,
  GetRecentPartsResponse,
  RecentPartGroup,
  GetPartPicturesResponse,
  CheckDrawingResponse,
  GetDrawingResponse,
  Printer,
  PrinterCreateRequest,
  PrinterUpdateRequest,
  ListPrintersResponse,
  PrinterMutationResponse,
  PrinterStatusResponse,
  UserPreferences,
  UserPreferencesResponse,
  OpenQuote,
  QuoteDashboardSummary,
  GetQuoteDashboardResponse,
  QuoteDetail,
  GetQuoteDetailResponse,
  CustomerSearchResult,
  CustomerDetail,
  CustomerQuoteHistoryItem,
  CustomerOrderHistoryItem,
  SearchCustomersResponse,
  GetCustomerDetailResponse,
  GetCustomerQuoteHistoryResponse,
  GetCustomerOrderHistoryResponse,
} from './types';

class ApiService {
  private async getBaseURL(): Promise<string> {
    const config = await environmentService.getConfig();
    return config.apiUrl;
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit
  ): Promise<T> {
    const baseURL = await this.getBaseURL();
    const url = `${baseURL}${endpoint}`;

    try {
      // Get the current user's JWT token
      const token = await getIdToken();

      // Merge headers with proper Content-Type handling
      const existingHeaders = options.headers as
        | Record<string, string>
        | undefined;
      const headers: Record<string, string> = {
        Authorization: token ? `Bearer ${token}` : '',
        ...existingHeaders,
      };

      // Set Content-Type for JSON requests if not already set
      // Don't set Content-Type for FormData - let browser set multipart/form-data
      if (
        options.body &&
        !headers['Content-Type'] &&
        !(options.body instanceof FormData)
      ) {
        headers['Content-Type'] = 'application/json';
      }

      const response = await fetch(url, {
        ...options,
        headers,
      });

      if (!response.ok) {
        // Read body once as text so we can still surface it if JSON.parse
        // fails (proxy errors, HTML error pages, empty body, etc.).
        const rawBody = await response.text().catch(() => '');
        let errorData: { message?: string; error?: string } = {};
        if (rawBody) {
          try {
            errorData = JSON.parse(rawBody);
          } catch {
            // body isn't JSON — fall through, we'll surface the raw text
          }
        }

        // API errors come back as { success:false, message, code } from
        // controllers/errorHelper, but a few endpoints still use { error }.
        const apiMessage = errorData.message || errorData.error;
        const bodyHint =
          !apiMessage && rawBody && rawBody.length <= 200 ? `: ${rawBody}` : '';
        const statusLabel = response.statusText
          ? `HTTP ${response.status} ${response.statusText}`
          : `HTTP ${response.status}`;
        const message = apiMessage || `${statusLabel}${bodyHint}`;

        const err = new Error(message) as Error & {
          status?: number;
          body?: string;
        };
        err.status = response.status;
        err.body = rawBody;
        throw err;
      }

      return await response.json();
    } catch (error) {
      // 4xx responses are expected client-side errors (e.g. the production
      // schedule API rejects dates far from today with 400). Log as a
      // warning instead of error so the console stays cleaner; callers can
      // still inspect the thrown error and decide what to do.
      const status = (error as { status?: number } | undefined)?.status;
      const msg = error instanceof Error ? error.message : String(error);
      const is4xx =
        (typeof status === 'number' && status >= 400 && status < 500) ||
        /HTTP\s*4\d{2}/.test(msg);
      if (is4xx) {
        console.warn(`API request failed for ${endpoint}:`, error);
      } else {
        console.error(`API request failed for ${endpoint}:`, error);
      }
      throw error;
    }
  }

  /**
   * Submit a generic job for async processing
   * @param workerType - Type of worker to process the job
   * @param request - Worker-specific request data
   * @param file - File to be processed
   * @returns JobSubmitResponse with jobId and initial status
   */
  async submitJob(
    workerType: WorkerType,
    request: JobRequest,
    file?: File
  ): Promise<JobSubmitResponse> {
    // If file is provided, use FormData (for listParser)
    if (file) {
      const formData = new FormData();
      formData.append('file', file);

      // Add all request fields to FormData
      Object.entries(request).forEach(([key, value]) => {
        if (value !== undefined && value !== null && key !== 'file') {
          // Handle nested objects like columnMappings
          if (typeof value === 'object') {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, String(value));
          }
        }
      });

      return this.makeRequest<JobSubmitResponse>(
        ENDPOINTS.JOBS_SUBMIT(workerType),
        {
          method: 'POST',
          body: formData,
        }
      );
    }

    // No file provided, send as JSON (for printer)
    return this.makeRequest<JobSubmitResponse>(
      ENDPOINTS.JOBS_SUBMIT(workerType),
      {
        method: 'POST',
        body: JSON.stringify(request),
      }
    );
  }

  /**
   * Submit a ListParser job for async processing
   * Convenience method for LIST_PARSER worker type
   */
  async submitListParserJob(
    request: ListParserRequest
  ): Promise<JobSubmitResponse> {
    return this.submitJob(WorkerType.LIST_PARSER, request, request.file);
  }

  /**
   * Get available price lists from Epicor
   * @returns Array of price lists with id, description, and currency
   */
  async getPriceLists(): Promise<PriceList[]> {
    const response = await this.makeRequest<GetPriceListsResponse>(
      ENDPOINTS.LIST_PARSER_PRICE_LISTS(),
      {
        method: 'GET',
      }
    );

    if (!response.success || !response.priceLists) {
      throw new Error(response.error || 'Failed to retrieve price lists');
    }

    return response.priceLists;
  }

  /**
   * Get a page of the rows of every file that went through the list parser
   * @param query - Filters and the cursor of the previous page (all optional)
   * @returns The rows, newest first, and the cursor for the next page when there may be more
   */
  async getListParserData(
    query: ListParserDataQuery = {}
  ): Promise<{ items: ListParserDataItem[]; nextCursor?: string }> {
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.append(key, String(value));
      }
    });

    const queryString = params.toString();
    const endpoint = `${ENDPOINTS.LIST_PARSER_DATA()}${
      queryString ? `?${queryString}` : ''
    }`;
    const response = await this.makeRequest<GetListParserDataResponse>(
      endpoint,
      { method: 'GET' }
    );

    if (!response.success || !response.items) {
      throw new Error(
        response.message || 'Failed to retrieve list parser data'
      );
    }

    return { items: response.items, nextCursor: response.nextCursor };
  }

  /**
   * Submit a Printer job for async processing
   * Convenience method for PRINTER worker type
   */
  async submitPrinterJob(
    request: PrinterJobRequest
  ): Promise<JobSubmitResponse> {
    return this.submitJob(WorkerType.PRINTER, request);
  }

  /**
   * Get the current status of a job
   */
  async getJobStatus(jobId: string): Promise<JobStatusResponse> {
    return this.makeRequest<JobStatusResponse>(ENDPOINTS.JOBS_STATUS(jobId), {
      method: 'GET',
    });
  }

  /**
   * Get presigned URL for downloading a completed job result
   */
  async getJobResult(jobId: string): Promise<JobResultResponse> {
    return this.makeRequest<JobResultResponse>(ENDPOINTS.JOBS_RESULT(jobId), {
      method: 'GET',
    });
  }

  /**
   * Poll job status until completion or failure
   * @param jobId - The job ID to poll
   * @param onStatusChange - Callback function called with full status response (optional)
   * @param pollInterval - Time in milliseconds between polls (default: 1000ms)
   * @param maxAttempts - Maximum number of polling attempts (default: 120)
   * @param abortSignal - Optional abort signal to cancel polling
   * @returns JobStatusResponse when job completes
   * @throws Error if job fails, times out, or invalid jobId
   */
  async pollJobStatus(
    jobId: string,
    onStatusChange?: (statusResponse: JobStatusResponse) => void,
    pollInterval: number = 1000,
    maxAttempts: number = 120,
    abortSignal?: AbortSignal
  ): Promise<JobStatusResponse> {
    let attempts = 0;

    while (attempts < maxAttempts) {
      // Check if abort signal has been triggered
      if (abortSignal?.aborted) {
        throw new Error('Job polling cancelled by user');
      }

      try {
        const statusData = await this.getJobStatus(jobId);
        const status = statusData.status;

        // Notify caller with full status response
        if (onStatusChange) {
          onStatusChange(statusData);
        }

        // Check if job is complete
        if (status === 'COMPLETED') {
          return statusData;
        } else if (status === 'FAILED') {
          const errorMsg = statusData.errorMessage || 'Job processing failed';
          throw new Error(errorMsg);
        }

        // Wait before polling again
        await new Promise((resolve) => setTimeout(resolve, pollInterval));
        attempts++;
      } catch (error) {
        // Don't re-throw abort errors, let them propagate as-is
        if (
          error instanceof Error &&
          error.message === 'Job polling cancelled by user'
        ) {
          throw error;
        }
        // Re-throw errors from job processing
        if (
          error instanceof Error &&
          error.message !== 'Job processing failed'
        ) {
          throw error;
        }
        throw error;
      }
    }

    throw new Error('Job processing timeout - exceeded maximum wait time');
  }

  /**
   * Get available parts from Epicor via search string with pagination
   * @param searchString - Required: space-separated keywords to search for parts
   * @param page - Page number (default: 1)
   * @param pageSize - Number of parts per page (default: 50)
   * @param includeInactives - Include inactive parts in results (default: false)
   * @returns Array of parts matching the search criteria with pagination metadata
   */
  async getParts(
    searchString: string,
    page: number = 1,
    pageSize: number = 50,
    includeInactives: boolean = false
  ): Promise<{ parts: Part[]; pagination: PaginationMeta }> {
    const params = new URLSearchParams();

    // Search string is mandatory
    if (!searchString || !searchString.trim()) {
      throw new Error('Search string is required to retrieve parts');
    }

    params.append('searchString', searchString.trim());
    params.append('page', page.toString());
    params.append('pageSize', pageSize.toString());
    params.append('includeInactives', includeInactives.toString());

    const queryString = params.toString();
    const endpoint = `${ENDPOINTS.PARTS_GET()}?${queryString}`;

    const response = await this.makeRequest<GetPartsResponse>(endpoint, {
      method: 'GET',
    });

    if (!response.success || !response.parts) {
      throw new Error(response.error || 'Failed to retrieve parts');
    }

    return {
      parts: response.parts,
      pagination: response.pagination || {
        page: 1,
        pageSize: 50,
        total: response.parts.length,
        totalPages: 1,
      },
    };
  }

  /**
   * Get all warehouse bins from Epicor
   * Used for bin label search and selection
   * @returns Array of all bins with binNum and description
   */
  async getBins(): Promise<Bin[]> {
    const response = await this.makeRequest<GetBinsResponse>(
      ENDPOINTS.BINS_GET(),
      {
        method: 'GET',
      }
    );

    if (!response.success || !response.bins) {
      throw new Error(response.error || 'Failed to retrieve bins');
    }

    return response.bins;
  }

  /**
   * Get detailed information for a specific part from Epicor
   * @param partNum - The part number to retrieve details for
   * @returns PartDetails object with all fields from BAQ_ExtDashboard_PartInfo
   */
  async getPartDetails(partNum: string): Promise<PartDetails> {
    const response = await this.makeRequest<GetPartDetailsResponse>(
      `${ENDPOINTS.PARTS_GET()}/${encodeURIComponent(partNum)}`,
      {
        method: 'GET',
      }
    );

    if (!response.success || !response.partDetails) {
      throw new Error(response.error || 'Failed to retrieve part details');
    }

    return response.partDetails;
  }

  /**
   * Look up ERP part number from UM part number (e.g., UMK-94129914 -> 117309)
   * @param partNumUM - The UM part number to look up
   * @returns The ERP part number if found, null otherwise
   */
  async lookupUMPart(partNumUM: string): Promise<string | null> {
    const response = await this.makeRequest<LookupUMPartResponse>(
      ENDPOINTS.PARTS_LOOKUP_UM(partNumUM),
      {
        method: 'GET',
      }
    );

    if (!response.success) {
      throw new Error(response.error || 'Failed to look up UM part number');
    }

    return response.found ? response.erpPartNum : null;
  }

  /**
   * Get warehouse bins for a specific part
   * @param partNum - The part number to retrieve bins for
   * @returns Array of warehouse bins
   */
  async getPartBins(partNum: string): Promise<PartBin[]> {
    const response = await this.makeRequest<GetPartBinsResponse>(
      ENDPOINTS.PARTS_BINS(partNum),
      {
        method: 'GET',
      }
    );

    if (!response.success || !response.partBins) {
      throw new Error(response.error || 'Failed to retrieve part bins');
    }

    return response.partBins;
  }

  /**
   * Get time phased demand for a specific part
   * @param partNum - The part number to retrieve time phased data for
   * @returns Array of time phased records
   */
  async getPartTimePhased(partNum: string): Promise<PartTimePhased[]> {
    const response = await this.makeRequest<GetPartTimePhasedResponse>(
      ENDPOINTS.PARTS_TIME_PHASED(partNum),
      {
        method: 'GET',
      }
    );

    if (!response.success || !response.timePhased) {
      throw new Error(response.error || 'Failed to retrieve time phased data');
    }

    return response.timePhased;
  }

  /**
   * Get purchase history for a specific part
   * @param partNum - The part number to retrieve purchase history for
   * @returns Array of purchase history records
   */
  async getPartPurchaseHist(partNum: string): Promise<PurchaseHist[]> {
    const response = await this.makeRequest<GetPartPurchaseHistResponse>(
      ENDPOINTS.PARTS_PURCHASE_HIST(partNum),
      {
        method: 'GET',
      }
    );

    if (!response.success || !response.purchaseHist) {
      throw new Error(response.error || 'Failed to retrieve purchase history');
    }

    return response.purchaseHist;
  }

  /**
   * Get approved suppliers for a specific part
   * @param partNum - The part number to retrieve approved suppliers for
   * @returns Array of approved supplier records
   */
  async getPartApprovedSuppliers(partNum: string): Promise<ApprovedSupplier[]> {
    const response = await this.makeRequest<GetPartApprovedSuppliersResponse>(
      ENDPOINTS.PARTS_APPROVED_SUPPLIERS(partNum),
      {
        method: 'GET',
      }
    );

    if (!response.success || !response.approvedSuppliers) {
      throw new Error(
        response.error || 'Failed to retrieve approved suppliers'
      );
    }

    return response.approvedSuppliers;
  }

  /**
   * Get transaction history for a specific part
   * @param partNum - The part number to retrieve transaction history for
   * @param page - Page number (default 1)
   * @param pageSize - Number of records per page (default 25)
   * @returns Transaction history response with pagination info
   */
  async getPartTransHist(
    partNum: string,
    page: number = 1,
    pageSize: number = 20
  ): Promise<GetPartTransHistResponse> {
    const url = `${ENDPOINTS.PARTS_TRANS_HIST(partNum)}?page=${page}&pageSize=${pageSize}`;
    const response = await this.makeRequest<GetPartTransHistResponse>(url, {
      method: 'GET',
    });

    if (!response.success || !response.transHist) {
      throw new Error(
        response.error || 'Failed to retrieve transaction history'
      );
    }

    return response;
  }

  /**
   * Get quote history for a specific part
   * @param partNum - The part number to retrieve quote history for
   * @returns Array of quote history records
   */
  async getPartQuoteHistory(partNum: string): Promise<QuoteHistory[]> {
    const response = await this.makeRequest<GetPartQuoteHistResponse>(
      ENDPOINTS.PARTS_QUOTE_HISTORY(partNum),
      {
        method: 'GET',
      }
    );

    if (!response.success || !response.quoteHistory) {
      throw new Error(response.error || 'Failed to retrieve quote history');
    }

    return response.quoteHistory;
  }

  /**
   * Get customer references for a specific part
   * @param partNum - The part number to retrieve customer references for
   * @returns Array of customer reference records
   */
  async getPartCustomerRefs(partNum: string): Promise<CustomerRef[]> {
    const response = await this.makeRequest<GetPartCustomerRefsResponse>(
      ENDPOINTS.PARTS_CUSTOMER_REFS(partNum),
      {
        method: 'GET',
      }
    );

    if (!response.success || !response.customerRefs) {
      throw new Error(
        response.error || 'Failed to retrieve customer references'
      );
    }

    return response.customerRefs;
  }

  /**
   * Get order history for a specific part
   * @param partNum - The part number to retrieve order history for
   * @returns Array of order history records
   */
  async getPartOrderHistory(partNum: string): Promise<OrderHistory[]> {
    const response = await this.makeRequest<GetPartOrderHistResponse>(
      ENDPOINTS.PARTS_ORDER_HISTORY(partNum),
      {
        method: 'GET',
      }
    );

    if (!response.success || !response.orderHistory) {
      throw new Error(response.error || 'Failed to retrieve order history');
    }

    return response.orderHistory;
  }

  /**
   * Get production history for a specific part
   * @param partNum - The ERP part number to look up
   * @returns Array of production history records
   */
  async getPartProdHistory(partNum: string): Promise<ProdHistory[]> {
    const response = await this.makeRequest<GetPartProdHistResponse>(
      ENDPOINTS.PARTS_PROD_HISTORY(partNum),
      {
        method: 'GET',
      }
    );

    if (!response.success || !response.prodHistory) {
      throw new Error(
        response.error || 'Failed to retrieve production history'
      );
    }

    return response.prodHistory;
  }

  /**
   * Get customer price list for a specific part
   * @param partNum - The ERP part number to look up
   * @returns Customer price list with tiered pricing
   */
  async getCustomerPriceList(partNum: string): Promise<CustomerPriceList> {
    const response = await this.makeRequest<GetCustomerPriceResponse>(
      ENDPOINTS.PARTS_CUSTOMER_PRICE(partNum),
      {
        method: 'GET',
      }
    );

    if (!response.success || !response.customerPriceList) {
      throw new Error(
        response.error || 'Failed to retrieve customer price list'
      );
    }

    return response.customerPriceList;
  }

  /**
   * Get fulfillment items with pagination, sorting, and search
   * @param offset - Starting index for pagination (default: 0)
   * @param limit - Number of items per page (default: 50)
   * @param sortBy - Field to sort by (optional)
   * @param sortOrder - Sort direction asc or desc (optional)
   * @param search - Search term to filter items (optional)
   * @returns Array of fulfillment items with pagination metadata
   */
  async getFulfillmentItems(
    offset: number = 0,
    limit: number = 50,
    sortBy?: string,
    sortOrder?: string,
    search?: string
  ): Promise<{
    items: FulfillmentItem[];
    pagination: FulfillmentPaginationMeta;
  }> {
    const params = new URLSearchParams();

    params.append('offset', offset.toString());
    params.append('limit', limit.toString());

    if (sortBy) {
      params.append('sortBy', sortBy);
    }

    if (sortOrder) {
      params.append('sortOrder', sortOrder);
    }

    if (search) {
      params.append('search', search);
    }

    const queryString = params.toString();
    let endpoint = ENDPOINTS.FULFILLMENT_GET();
    if (queryString) {
      endpoint += `?${queryString}`;
    }

    const response = await this.makeRequest<GetFulfillmentItemsResponse>(
      endpoint,
      {
        method: 'GET',
      }
    );

    if (!response.success || !response.items) {
      throw new Error(response.error || 'Failed to retrieve fulfillment items');
    }

    return {
      items: response.items,
      pagination: response.pagination || {
        offset: 0,
        limit: 50,
        total: 0,
        totalBacklog: 0,
      },
    };
  }

  /**
   * Get method/BOM tree for a specific part with lazy loading support
   * @param partNum - The part number to retrieve method/BOM for
   * @returns Method/BOM tree structure
   */
  async getMethod(partNum: string): Promise<MethodNode> {
    const endpoint = ENDPOINTS.PARTS_METHOD(partNum);

    const response = await this.makeRequest<GetMethodResponse>(endpoint, {
      method: 'GET',
    });

    if (!response.success || !response.methodData) {
      throw new Error(response.error || 'Failed to retrieve method/BOM tree');
    }

    return response.methodData;
  }

  /**
   * Check if a drawing PDF is available for a given drawing number
   * @param drawNum - The drawing number to check
   * @returns true if the drawing is available, false otherwise
   */
  async checkDrawingAvailable(drawNum: string): Promise<boolean> {
    try {
      const endpoint = ENDPOINTS.PARTS_DRAWING_CHECK(drawNum);
      const response = await this.makeRequest<CheckDrawingResponse>(endpoint, {
        method: 'GET',
      });
      return response.success && response.available;
    } catch {
      return false;
    }
  }

  /**
   * Get a presigned URL for the drawing PDF and open it in a new tab.
   * The API returns a short-lived S3 URL fronted by the Drawing Lambda
   * that fetches the PDF from the on-prem QNAP.
   */
  async openDrawingPdf(drawNum: string): Promise<void> {
    const endpoint = ENDPOINTS.PARTS_DRAWING(drawNum);
    const response = await this.makeRequest<GetDrawingResponse>(endpoint, {
      method: 'GET',
    });

    if (!response.success || !response.url) {
      throw new Error(response.error || 'Failed to fetch drawing');
    }

    window.open(response.url, '_blank');
  }

  /**
   * Get where used data for a specific part (parent assemblies that use this part)
   * @param partNum - The part number to retrieve where used data for
   * @returns Array of where used records
   */
  async getPartWhereUsed(partNum: string): Promise<WhereUsed[]> {
    const endpoint = ENDPOINTS.PARTS_WHERE_USED(partNum);

    const response = await this.makeRequest<GetWhereUsedResponse>(endpoint, {
      method: 'GET',
    });

    if (!response.success || !response.whereUsed) {
      throw new Error(response.error || 'Failed to retrieve where used data');
    }

    return response.whereUsed;
  }

  /**
   * Generate printer preview image
   * @param request - Printer preview request with labelType, parameters, and quantity
   * @param signal - Optional AbortSignal to cancel the request
   * @returns PNG image as blob for display
   */
  async generatePrinterPreview(
    request: PrinterPreviewRequest,
    signal?: AbortSignal
  ): Promise<Blob> {
    const baseURL = await this.getBaseURL();
    const endpoint = ENDPOINTS.PRINTER_PREVIEW();
    const url = `${baseURL}${endpoint}`;

    try {
      // Get the current user's JWT token
      const token = await getIdToken();

      const headers: Record<string, string> = {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
      };

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
        signal, // Pass abort signal to fetch
      });

      if (!response.ok) {
        // Read body once as text so we can still surface it if JSON.parse
        // fails (proxy errors, HTML error pages, empty body, etc.).
        const rawBody = await response.text().catch(() => '');
        let errorData: { message?: string; error?: string } = {};
        if (rawBody) {
          try {
            errorData = JSON.parse(rawBody);
          } catch {
            // body isn't JSON — fall through, we'll surface the raw text
          }
        }
        const apiMessage = errorData.message || errorData.error;
        const bodyHint =
          !apiMessage && rawBody && rawBody.length <= 200 ? `: ${rawBody}` : '';
        const statusLabel = response.statusText
          ? `HTTP ${response.status} ${response.statusText}`
          : `HTTP ${response.status}`;
        const message = apiMessage || `${statusLabel}${bodyHint}`;

        const err = new Error(message) as Error & {
          status?: number;
          body?: string;
        };
        err.status = response.status;
        err.body = rawBody;
        throw err;
      }

      // Return PNG blob for image display
      return await response.blob();
    } catch (error) {
      console.error(`API request failed for ${endpoint}:`, error);
      throw error;
    }
  }

  async checkPrinterHealth(printerId: string): Promise<{
    status: 'online' | 'offline';
    ip: string;
    port: number;
    timestamp: string;
  }> {
    const response = await this.makeRequest<PrinterStatusResponse>(
      ENDPOINTS.PRINTERS_STATUS(printerId),
      { method: 'GET' }
    );
    if (!response.success || !response.status) {
      throw new Error(response.error || 'Failed to check printer status');
    }
    return {
      status: response.status,
      ip: response.ip || '',
      port: response.port || 0,
      timestamp: response.timestamp || new Date().toISOString(),
    };
  }

  async listPrinters(): Promise<Printer[]> {
    const response = await this.makeRequest<ListPrintersResponse>(
      ENDPOINTS.PRINTERS_LIST(),
      { method: 'GET' }
    );
    if (!response.success || !response.printers) {
      throw new Error(response.error || 'Failed to list printers');
    }
    return response.printers;
  }

  async createPrinter(request: PrinterCreateRequest): Promise<Printer> {
    const response = await this.makeRequest<PrinterMutationResponse>(
      ENDPOINTS.PRINTERS_CREATE(),
      {
        method: 'POST',
        body: JSON.stringify(request),
      }
    );
    if (!response.success || !response.printer) {
      throw new Error(response.error || 'Failed to create printer');
    }
    return response.printer;
  }

  async updatePrinter(
    printerId: string,
    request: PrinterUpdateRequest
  ): Promise<Printer> {
    const response = await this.makeRequest<PrinterMutationResponse>(
      ENDPOINTS.PRINTERS_UPDATE(printerId),
      {
        method: 'PUT',
        body: JSON.stringify(request),
      }
    );
    if (!response.success || !response.printer) {
      throw new Error(response.error || 'Failed to update printer');
    }
    return response.printer;
  }

  async disablePrinter(printerId: string): Promise<Printer> {
    const response = await this.makeRequest<PrinterMutationResponse>(
      ENDPOINTS.PRINTERS_DELETE(printerId),
      { method: 'DELETE' }
    );
    if (!response.success || !response.printer) {
      throw new Error(response.error || 'Failed to disable printer');
    }
    return response.printer;
  }

  /**
   * Permanently remove a printer. The API rejects the call unless the
   * printer is already soft-disabled, so the admin always opts in via
   * the two-step UI: Disable -> Delete.
   */
  async forceDeletePrinter(printerId: string): Promise<void> {
    const response = await this.makeRequest<{
      success: boolean;
      deleted?: boolean;
      error?: string;
    }>(ENDPOINTS.PRINTERS_FORCE_DELETE(printerId), { method: 'DELETE' });
    if (!response.success) {
      throw new Error(response.error || 'Failed to delete printer');
    }
  }

  async getUserPreferences(): Promise<UserPreferences> {
    const response = await this.makeRequest<UserPreferencesResponse>(
      ENDPOINTS.USER_PREFERENCES(),
      { method: 'GET' }
    );
    if (!response.success || !response.preferences) {
      throw new Error(response.error || 'Failed to load user preferences');
    }
    return response.preferences;
  }

  async setLastPrinterId(printerId: string): Promise<UserPreferences> {
    const response = await this.makeRequest<UserPreferencesResponse>(
      ENDPOINTS.USER_PREFERENCES(),
      {
        method: 'PUT',
        body: JSON.stringify({ printerId }),
      }
    );
    if (!response.success || !response.preferences) {
      throw new Error(response.error || 'Failed to update user preferences');
    }
    return response.preferences;
  }

  /**
   * Get sales order details by order number
   * @param orderNum - The sales order number to retrieve
   * @returns SalesOrder with header and lines
   */
  async getSalesOrder(orderNum: number): Promise<SalesOrder> {
    const endpoint = ENDPOINTS.SALES_ORDER_GET(orderNum);

    const response = await this.makeRequest<GetSalesOrderResponse>(endpoint, {
      method: 'GET',
    });

    if (!response.success || !response.salesOrder) {
      throw new Error(response.error || 'Failed to retrieve sales order');
    }

    return response.salesOrder;
  }

  /**
   * Get the shipments (pack slips) of a sales order
   * @param orderNum - The sales order number
   * @returns Shipments with their packed lines
   */
  async getSalesOrderShipments(
    orderNum: number
  ): Promise<SalesOrderShipment[]> {
    const response = await this.makeRequest<GetSalesOrderShipmentsResponse>(
      ENDPOINTS.SALES_ORDER_SHIPMENTS(orderNum),
      {
        method: 'GET',
      }
    );

    if (!response.success || !response.shipments) {
      throw new Error(response.error || 'Failed to retrieve shipments');
    }

    return response.shipments;
  }

  /**
   * Get purchase order details by PO number
   * @param poNum - The purchase order number to retrieve
   * @returns PurchaseOrder with header and lines (releases grouped per line).
   * Rejects with an error whose `status` is 404 when the PO does not exist.
   */
  async getPurchaseOrder(poNum: number): Promise<PurchaseOrder> {
    const response = await this.makeRequest<GetPurchaseOrderResponse>(
      ENDPOINTS.PURCHASE_ORDER_GET(poNum),
      {
        method: 'GET',
      }
    );

    if (!response.success || !response.purchaseOrder) {
      throw new Error(response.error || 'Failed to retrieve purchase order');
    }

    return response.purchaseOrder;
  }

  /**
   * Get the receipts (packing slips received) of a purchase order
   * @param poNum - The purchase order number
   * @returns Receipts with their received lines, oldest first
   */
  async getPurchaseOrderReceipts(
    poNum: number
  ): Promise<PurchaseOrderReceipt[]> {
    const response = await this.makeRequest<GetPurchaseOrderReceiptsResponse>(
      ENDPOINTS.PURCHASE_ORDER_RECEIPTS(poNum),
      {
        method: 'GET',
      }
    );

    if (!response.success || !response.receipts) {
      throw new Error(response.error || 'Failed to retrieve receipts');
    }

    return response.receipts;
  }

  /**
   * Generate Pick List PDF for a sales order and return presigned URL
   * @param orderNum - The sales order number
   * @returns Presigned URL response for downloading the PDF
   */
  async getPickListPdf(orderNum: number): Promise<{
    presignedUrl: string;
    s3Key: string;
    expiresAt: string;
  }> {
    const response = await this.makeRequest<{
      success: boolean;
      presignedUrl: string;
      s3Key: string;
      expiresAt: string;
      error?: string;
    }>(ENDPOINTS.SALES_ORDER_PICK_LIST(orderNum), {
      method: 'GET',
    });

    if (!response.success || !response.presignedUrl) {
      throw new Error(response.error || 'Failed to generate pick list PDF');
    }

    return {
      presignedUrl: response.presignedUrl,
      s3Key: response.s3Key,
      expiresAt: response.expiresAt,
    };
  }

  /**
   * Get production schedule from Epicor SchedulingBoardSvc
   * @param startDate - Start date in YYYY-MM-DD format
   * @param endDate - End date in YYYY-MM-DD format
   * @param resourceGrpID - Optional resource group filter (e.g., 'TN', 'FN', 'CONVEN')
   * @returns Array of scheduling board entries
   */
  async getProductionSchedule(
    startDate: string,
    endDate: string,
    resourceGrpID?: string
  ): Promise<SchedulingBoardEntry[]> {
    const endpoint = ENDPOINTS.SCHEDULING_PRODUCTION(
      startDate,
      endDate,
      resourceGrpID
    );

    const response = await this.makeRequest<GetProductionScheduleResponse>(
      endpoint,
      {
        method: 'GET',
      }
    );

    if (!response.success || !response.schedulingBoard) {
      throw new Error(
        response.error || 'Failed to retrieve production schedule'
      );
    }

    return response.schedulingBoard;
  }

  /**
   * Get production calendar from Epicor ProdCalSvc
   * @param calendarID - The calendar ID (e.g., '2-SHIFTS')
   * @returns Production calendar with hours array (168 booleans for each hour of the week)
   */
  async getProductionCalendar(calendarID: string): Promise<ProductionCalendar> {
    const endpoint = ENDPOINTS.SCHEDULING_CALENDAR(calendarID);

    const response = await this.makeRequest<GetProductionCalendarResponse>(
      endpoint,
      {
        method: 'GET',
      }
    );

    if (!response.success || !response.calendar) {
      throw new Error(
        response.error || 'Failed to retrieve production calendar'
      );
    }

    return response.calendar;
  }

  /**
   * Get job data including header, operations, and materials from Epicor
   * @param jobNum - Job number
   * @returns Job dataset with header, operations, and materials
   */
  async getJobData(jobNum: string): Promise<JobDataset> {
    const endpoint = ENDPOINTS.JOB_TRACKER_GET(jobNum);

    const response = await this.makeRequest<GetJobDataResponse>(endpoint, {
      method: 'GET',
    });

    if (!response.success || !response.jobData) {
      throw new Error(response.error || 'Failed to retrieve job data');
    }

    return response.jobData;
  }

  /**
   * Get PO releases for subcontract operations on a job
   * @param jobNum - Job number
   * @returns Array of PO release records
   */
  async getJobPORels(jobNum: string): Promise<PORelInfo[]> {
    const response = await this.makeRequest<GetJobPORelsResponse>(
      ENDPOINTS.JOB_TRACKER_PO_RELS(jobNum),
      { method: 'GET' }
    );

    if (!response.success || !response.poRels) {
      throw new Error(response.error || 'Failed to retrieve PO releases');
    }

    return response.poRels;
  }

  /**
   * Generate Pick List PDF for a job and return presigned URL
   * @param jobNum - The job number
   * @returns Presigned URL response for downloading the PDF
   */
  async getJobPickListPdf(jobNum: string): Promise<{
    presignedUrl: string;
    s3Key: string;
    expiresAt: string;
  }> {
    const response = await this.makeRequest<{
      success: boolean;
      presignedUrl: string;
      s3Key: string;
      expiresAt: string;
      error?: string;
    }>(ENDPOINTS.JOB_TRACKER_PICK_LIST(jobNum), {
      method: 'GET',
    });

    if (!response.success || !response.presignedUrl) {
      throw new Error(response.error || 'Failed to generate job pick list PDF');
    }

    return {
      presignedUrl: response.presignedUrl,
      s3Key: response.s3Key,
      expiresAt: response.expiresAt,
    };
  }

  /**
   * Get part details for the picture processor part confirmation step
   * @param partNum - The ERP part number to look up
   * @returns PartDetails with part (UD_PartNumUM_c) and descriptionFR (PartDescription)
   */
  async getPart(partNum: string): Promise<PartDetails> {
    return this.getPartDetails(partNum);
  }

  /**
   * Submit a photo for a part to the picture processor
   * @param file - The image file to upload
   * @param partNumber - The part number associated with the photo
   * @returns SubmitPictureResponse with jobId and status
   */
  async submitPicture(
    file: File,
    partNumber: string
  ): Promise<SubmitPictureResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('partNumber', partNumber);

    const response = await this.makeRequest<
      SubmitPictureResponse & { success: boolean; error?: string }
    >(ENDPOINTS.PICTURE_PROCESSOR_SUBMIT(), {
      method: 'POST',
      body: formData,
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to submit picture');
    }

    return {
      jobId: response.jobId,
      partNumber: response.partNumber,
      status: response.status,
    };
  }

  /**
   * Get recently uploaded pictures across all parts
   * @param page - Page number (default 1)
   * @param limit - Items per page (default 20)
   * @param partNumber - Optional part number filter
   */
  async getRecentPictures(
    page: number = 1,
    limit: number = 20,
    partNumber?: string
  ): Promise<{
    pictures: PictureItem[];
    total: number;
    page: number;
    totalPages: number;
  }> {
    const response = await this.makeRequest<GetRecentPicturesResponse>(
      ENDPOINTS.PICTURE_MANAGER_RECENT(page, limit, partNumber),
      { method: 'GET' }
    );

    if (!response.success || !response.pictures) {
      throw new Error(response.error || 'Failed to retrieve recent pictures');
    }

    return {
      pictures: response.pictures,
      total: response.total ?? 0,
      page: response.page ?? page,
      totalPages: response.totalPages ?? 1,
    };
  }

  /**
   * Get recently updated parts grouped by part number, with all pictures per part.
   * Sorted by most recent upload descending. Paginated by parts.
   */
  async getRecentParts(
    page: number = 1,
    limit: number = 10,
    partNumber?: string
  ): Promise<{
    parts: RecentPartGroup[];
    total: number;
    page: number;
    totalPages: number;
  }> {
    const response = await this.makeRequest<GetRecentPartsResponse>(
      ENDPOINTS.PICTURE_MANAGER_RECENT_PARTS(page, limit, partNumber),
      { method: 'GET' }
    );

    if (!response.success || !response.parts) {
      throw new Error(response.error || 'Failed to retrieve recent parts');
    }

    return {
      parts: response.parts,
      total: response.total ?? 0,
      page: response.page ?? page,
      totalPages: response.totalPages ?? 1,
    };
  }

  /**
   * Get all pictures for a specific part
   * @param partNumber - The part number to retrieve pictures for
   */
  async getPartPictures(partNumber: string): Promise<PictureItem[]> {
    const response = await this.makeRequest<GetPartPicturesResponse>(
      ENDPOINTS.PICTURE_MANAGER_PART(partNumber),
      { method: 'GET' }
    );

    if (!response.success || !response.pictures) {
      throw new Error(response.error || 'Failed to retrieve part pictures');
    }

    return response.pictures;
  }

  /**
   * Reorder pictures for a specific part
   * @param partNumber - The part number
   * @param order - New order array (e.g. [2, 0, 1] means current photo 2 becomes index 0, etc.)
   */
  async reorderPictures(partNumber: string, order: number[]): Promise<void> {
    const response = await this.makeRequest<{
      success: boolean;
      error?: string;
    }>(ENDPOINTS.PICTURE_MANAGER_REORDER(partNumber), {
      method: 'POST',
      body: JSON.stringify({ order }),
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to reorder pictures');
    }
  }

  /**
   * Delete a picture for a specific part by index
   * @param partNumber - The part number
   * @param index - The 0-based index of the picture to delete
   */
  async deletePicture(partNumber: string, index: number): Promise<void> {
    const response = await this.makeRequest<{
      success: boolean;
      error?: string;
    }>(ENDPOINTS.PICTURE_MANAGER_DELETE(partNumber, index), {
      method: 'DELETE',
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to delete picture');
    }
  }

  // ===== Quote Dashboard =====

  /**
   * Fetch all open quotes with follow-up agent status and summary
   */
  async getOpenQuotes(): Promise<{
    quotes: OpenQuote[];
    summary: QuoteDashboardSummary;
  }> {
    const response = await this.makeRequest<GetQuoteDashboardResponse>(
      ENDPOINTS.QUOTES_GET(),
      { method: 'GET' }
    );

    if (!response.success || !response.quotes || !response.summary) {
      throw new Error(response.error || 'Failed to retrieve open quotes');
    }

    return { quotes: response.quotes, summary: response.summary };
  }

  /**
   * Fetch quote detail (header + line items) for drill-down
   * @param quoteNum - The quote number
   */
  async getQuoteDetail(quoteNum: number): Promise<QuoteDetail> {
    const response = await this.makeRequest<GetQuoteDetailResponse>(
      ENDPOINTS.QUOTE_DETAIL(quoteNum),
      { method: 'GET' }
    );

    if (!response.success || !response.quoteDetail) {
      throw new Error(response.error || 'Failed to retrieve quote detail');
    }

    return response.quoteDetail;
  }

  /**
   * Search customers by name or customer ID for the customer dashboard selector
   */
  async searchCustomers(query: string): Promise<CustomerSearchResult[]> {
    const response = await this.makeRequest<SearchCustomersResponse>(
      ENDPOINTS.CUSTOMERS_SEARCH(query),
      { method: 'GET' }
    );

    if (!response.success || !response.customers) {
      throw new Error(response.error || 'Failed to search customers');
    }

    return response.customers;
  }

  /**
   * Get customer header info and ship-to locations
   */
  async getCustomerDetail(custNum: number): Promise<CustomerDetail> {
    const response = await this.makeRequest<GetCustomerDetailResponse>(
      ENDPOINTS.CUSTOMER_DETAIL(custNum),
      { method: 'GET' }
    );

    if (!response.success || !response.customerDetail) {
      throw new Error(response.error || 'Failed to retrieve customer detail');
    }

    return response.customerDetail;
  }

  /**
   * Get a customer's quote history, optionally scoped to a ship-to
   */
  async getCustomerQuoteHistory(
    custNum: number,
    shipToNum?: string
  ): Promise<CustomerQuoteHistoryItem[]> {
    const response = await this.makeRequest<GetCustomerQuoteHistoryResponse>(
      ENDPOINTS.CUSTOMER_QUOTES(custNum, shipToNum),
      { method: 'GET' }
    );

    if (!response.success || !response.quoteHistory) {
      throw new Error(
        response.error || 'Failed to retrieve customer quote history'
      );
    }

    return response.quoteHistory;
  }

  /**
   * Get a customer's order history, optionally scoped to a ship-to
   */
  async getCustomerOrderHistory(
    custNum: number,
    shipToNum?: string
  ): Promise<CustomerOrderHistoryItem[]> {
    const response = await this.makeRequest<GetCustomerOrderHistoryResponse>(
      ENDPOINTS.CUSTOMER_ORDERS(custNum, shipToNum),
      { method: 'GET' }
    );

    if (!response.success || !response.orderHistory) {
      throw new Error(
        response.error || 'Failed to retrieve customer order history'
      );
    }

    return response.orderHistory;
  }

  // ===== Quote-to-Cash Dashboard (admins) =====

  /** One dashboard section; the API answers its fields beside `success` */
  private async getQuoteToCashSection<T>(endpoint: string): Promise<T> {
    const response = await this.makeRequest<
      T & { success: boolean; message?: string }
    >(endpoint, { method: 'GET' });
    if (!response.success) {
      throw new Error(response.message || 'Failed to load the dashboard');
    }
    return response;
  }

  /** Quotes and orders by month or week, with orders forecast from quotes */
  getQuoteForecast(view: QuoteToCashView): Promise<QuoteForecast> {
    return this.getQuoteToCashSection(
      ENDPOINTS.QUOTE_TO_CASH('quote-forecast', view)
    );
  }

  /** This fiscal year's order intake, cumulative, against last year and the target */
  getYtdSales(view: QuoteToCashView): Promise<YtdSection> {
    return this.getQuoteToCashSection(
      ENDPOINTS.QUOTE_TO_CASH('ytd-sales', view)
    );
  }

  /** This fiscal year's order intake by customer and ship-to, against targets */
  getCustomerSales(): Promise<CustomerSalesSection> {
    return this.getQuoteToCashSection(
      ENDPOINTS.QUOTE_TO_CASH('customer-sales')
    );
  }

  /** This fiscal year's invoicing, cumulative, against last year */
  getYtdInvoicing(view: QuoteToCashView): Promise<YtdSection> {
    return this.getQuoteToCashSection(
      ENDPOINTS.QUOTE_TO_CASH('ytd-invoicing', view)
    );
  }

  // ===== Part Demand & Forecast Dashboard =====

  /** A part's demand history, forecast and planning context */
  async getPartDemand(partNum: string): Promise<PartDemand> {
    const response = await this.makeRequest<
      PartDemand & { success: boolean; message?: string }
    >(ENDPOINTS.PART_DEMAND(partNum), { method: 'GET' });
    if (!response.success) {
      throw new Error(response.message || 'Failed to load part demand');
    }
    return response;
  }

  // ===== Sales inbox agent =====

  /** A month's (YYYY-MM) emails handled by the sales inbox agent, with the review summary */
  async getSalesAgentEmails(month: string): Promise<SalesAgentEmails> {
    const response = await this.makeRequest<
      SalesAgentEmails & { success: boolean; message?: string }
    >(ENDPOINTS.SALES_AGENT_EMAILS(month), { method: 'GET' });
    if (!response.success) {
      throw new Error(response.message || 'Failed to load the emails');
    }
    return response;
  }

  /** Records the category an email should have had */
  async reviewSalesAgentEmail(
    emailKey: string,
    category: SalesAgentCategory
  ): Promise<void> {
    const response = await this.makeRequest<{
      success: boolean;
      message?: string;
    }>(ENDPOINTS.SALES_AGENT_REVIEW(emailKey), {
      method: 'POST',
      body: JSON.stringify({ category }),
    });
    if (!response.success) {
      throw new Error(response.message || 'Failed to save the review');
    }
  }

  /** Approves (with the Epicor quote number) or rejects an RFQ proposal */
  async decideSalesAgentApproval(
    emailKey: string,
    decision: SalesAgentApprovalRequest
  ): Promise<void> {
    const response = await this.makeRequest<{
      success: boolean;
      message?: string;
    }>(ENDPOINTS.SALES_AGENT_APPROVAL(emailKey), {
      method: 'POST',
      body: JSON.stringify(decision),
    });
    if (!response.success) {
      throw new Error(response.message || 'Failed to record the decision');
    }
  }
}

// Create a singleton instance
export const apiService = new ApiService();
export default apiService;
